import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Offer } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import UTCTime from '../UTCTime/UTCTime';
import PatientListModal from '../PatientListModal/PatientListModal';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import './OfferItem.css';

interface OfferItemProps {
  offer: Offer;
  requestId: string;
  requestStatus: string;
  requestCategory?: string;
  requestCreatorId?: string;
  totalPatients: number;
  canAccept: boolean;
  canWithdraw: boolean;
  canEdit: boolean;
  canViewDetails: boolean;
  onAccept: (offerId: string) => void;
  onWithdraw: (offerId: string) => void;
  onEdit: (offerId: string) => void;
}

function OfferItem({
  offer,
  requestId,
  requestStatus,
  requestCategory,
  requestCreatorId,
  totalPatients,
  canAccept,
  canWithdraw,
  canEdit,
  canViewDetails,
  onAccept,
  onWithdraw,
  onEdit
}: OfferItemProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showPatientList, setShowPatientList] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  // Calculate offer type statistics (supports legacy + new types)
  const getOfferTypeStats = () => {
    if (!offer.offerPatients || offer.offerPatients.length === 0) {
      return null;
    }

    const stats = {
      full: 0,           // BOTH or TREATMENT_AND_TRANSPORT (full offer)
      extTransport: 0,    // TRANSPORT (treatment + external transport)
      treatmentOnly: 0,   // TREATMENT or TREATMENT_ONLY
      transportExtension: 0, // TRANSPORT_EXTENSION (provide transport)
      totalCompanions: 0
    };

    offer.offerPatients.forEach(op => {
      const type = op.offerType || 'TREATMENT';
      if (type === 'BOTH' || type === 'TREATMENT_AND_TRANSPORT') stats.full++;
      else if (type === 'TRANSPORT') stats.extTransport++;
      else if ((type === 'TREATMENT' || type === 'TREATMENT_ONLY') && op.transportMode === 'PROVIDED_BY_PARTNER_NATION') stats.extTransport++;
      else if (type === 'TREATMENT' || type === 'TREATMENT_ONLY') stats.treatmentOnly++;
      else if (type === 'TRANSPORT_EXTENSION') stats.transportExtension++;
      if (op.patient?.patientCompanionsCount > 0) {
        stats.totalCompanions += op.patient.patientCompanionsCount;
      }
    });

    return stats;
  };

  const offerTypeStats = getOfferTypeStats();
  const patientCount = offer.offerPatients?.length || 0;
  const percentage = totalPatients > 0 ? Math.round((patientCount / totalPatients) * 100) : 0;

  return (
    <>
      <div className={`offer-item-card ${offer.status === 'ACCEPTED' ? 'accepted' : ''}`}>
        {/* Row 1: Flag+Nation | Patients count | Date */}
        <div className="offer-row">
          <div className="offer-cell offer-cell-nation">
            {offer.user.flagImage && (
              <img
                src={offer.user.flagImage}
                alt={offer.user.nation}
                className="offer-flag"
              />
            )}
            <span className="nation-name">{offer.user.nation}</span>
          </div>
          <div className="offer-cell offer-cell-patients">
            <span className="patients-label">{t('requests.patients')}:</span>
            <span className="patients-count">{patientCount}</span>
            <span className="patients-total">{t('common.of')} {totalPatients} ({percentage}%)</span>
            {offerTypeStats && offerTypeStats.totalCompanions > 0 && (
              <span className="patients-companions">+ {offerTypeStats.totalCompanions} comp.</span>
            )}
          </div>
          <div className="offer-cell offer-cell-date">
            <UTCTime date={offer.updatedAt || offer.createdAt} />
          </div>
        </div>

        {/* Row 2: Status | Offer Breakdown | Actions */}
        <div className="offer-row">
          <div className="offer-cell offer-cell-status">
            {offer.status === 'ACCEPTED' && (
              <span className="badge badge-success">{t('status.accepted')}</span>
            )}
            {offer.status === 'PENDING' && (
              <span className="badge badge-pending">{t('status.pending')}</span>
            )}
            {(offer.status as string) === 'PUBLISHED' && (
              <span className="badge badge-published">{t('status.published')}</span>
            )}
          </div>
          <div className="offer-cell offer-cell-breakdown">
            {offerTypeStats && (
              <span className="offer-type-stats">
                {offerTypeStats.full > 0 && (
                  <span className="offer-type-stat" title="Full Offer (Treatment & Transport)">
                    <img src="/icons/offer_combined_gruen.webp" alt="Full Offer" width={36} height={20} />
                    {offerTypeStats.full}
                  </span>
                )}
                {offerTypeStats.extTransport > 0 && (
                  <span className="offer-type-stat" title="Treatment + External Transport">
                    <img src="/icons/delivery_gelb.webp" alt="Ext. Transport" width={20} height={20} />
                    <img src="/icons/hospital_gruen.webp" alt="Treatment" width={20} height={20} />
                    {offerTypeStats.extTransport}
                    {offer.transportProviderNationId && (
                      <span className="transport-partner-badge">{offer.transportProviderNationId}</span>
                    )}
                  </span>
                )}
                {offerTypeStats.treatmentOnly > 0 && (
                  <span className="offer-type-stat" title="Treatment Only">
                    <img src="/icons/delivery_leer.webp" alt="No Transport" width={20} height={20} />
                    <img src="/icons/hospital_gruen.webp" alt="Treatment" width={20} height={20} />
                    {offerTypeStats.treatmentOnly}
                  </span>
                )}
                {offerTypeStats.transportExtension > 0 && (
                  <span className="offer-type-stat" title="Provide Transport">
                    <img src="/icons/delivery_gruen.webp" alt="Transport" width={20} height={20} />
                    <img src="/icons/hospital_gelb.webp" alt="Other Nation Treatment" width={20} height={20} />
                    {offerTypeStats.transportExtension}
                  </span>
                )}
              </span>
            )}
            {offer.receivingStartPoint && (
              <span className="offer-hub-info">
                Hub: {offer.receivingStartPoint.name}
                {offer.receivingStartPoint.nation && (
                  <span className="hub-nation"> ({offer.receivingStartPoint.nation})</span>
                )}
              </span>
            )}
          </div>
          <div className="offer-cell offer-cell-actions">
            {canViewDetails && (
              <button
                onClick={() => setShowPatientList(true)}
                className="btn btn-outline btn-sm"
              >
                {t('common.viewDetails')}
              </button>
            )}
            {canEdit && offer.status === 'PENDING' && (
              <button
                onClick={() => onEdit(offer.id)}
                className="btn btn-outline btn-sm"
              >
                {t('common.edit')}
              </button>
            )}
            {canAccept && offer.status === 'PENDING' && requestCategory !== 'MEDEVAC' && (
              <button
                onClick={() => onAccept(offer.id)}
                className="btn btn-success btn-sm"
              >
                {t('offers.acceptOffer')}
              </button>
            )}
            {canWithdraw && offer.status === 'PENDING' && (
              <button
                onClick={() => setShowWithdrawConfirm(true)}
                className="btn btn-danger btn-sm"
              >
                {t('offers.withdrawOffer')}
              </button>
            )}
          </div>
        </div>

        {/* Transport mode row */}
        {offer.transportMode && (
          <div className="offer-info-row">
            <span className="info-label">{t('offers.transport')}:</span>
            {offer.transportMode === 'INCLUDED_BY_OFFERING_NATION' && (
              <span className="transport-mode-badge mode-included">Own Nation</span>
            )}
            {offer.transportMode === 'PROVIDED_BY_PARTNER_NATION' && (
              <span className="transport-mode-badge mode-partner">
                Partner Nation
                {offer.transportProviderNationId && (
                  <span className="partner-nation-name"> ({offer.transportProviderNationId})</span>
                )}
              </span>
            )}
            {offer.transportMode === 'OPEN' && (
              <span className="transport-mode-badge mode-open">Open</span>
            )}
          </div>
        )}

        {/* Transport agreement note */}
        {offer.transportAgreementNote && (
          <div className="offer-info-row">
            <span className="info-label">{t('common.notes')}:</span>
            <span className="info-text">{offer.transportAgreementNote}</span>
          </div>
        )}

        {/* Extension offers (linked transport offers from other nations) */}
        {offer.extensionOffers && offer.extensionOffers.length > 0 && (
          <div className="offer-info-row">
            <span className="info-label">{t('offers.transportOffer')}:</span>
            <span className="extension-offers">
              {offer.extensionOffers.map(ext => (
                <span key={ext.id} className="extension-offer-chip">
                  {ext.user.flagImage && (
                    <img src={ext.user.flagImage} alt={ext.user.nation} className="ext-offer-flag" />
                  )}
                  {ext.user.nation}
                  <span className={`ext-offer-status ${ext.status === 'ACCEPTED' ? 'accepted' : ''}`}>
                    {ext.status === 'ACCEPTED' ? 'Accepted' : ext.status === 'PENDING' ? 'Pending' : ext.status}
                  </span>
                </span>
              ))}
            </span>
            {offer.receivingStartPoint && (
              <span className="ext-offer-hub">Exit Hub: {offer.receivingStartPoint.name}</span>
            )}
          </div>
        )}

        {/* Comment row (only if comment exists) */}
        {offer.comment && (
          <div className="offer-info-row">
            <span className="info-label">{t('common.comments')}:</span>
            <span className="info-text">{offer.comment}</span>
          </div>
        )}
      </div>

      {/* Patient List Modal - Filtered to offer patients (transport partner sees only their transport patients) */}
      {showPatientList && (
        <PatientListModal
          requestId={requestId}
          requestStatus={requestStatus}
          requestCreatorId={requestCreatorId}
          requestCategory={requestCategory}
          onClose={() => setShowPatientList(false)}
          offerPatientIds={
            offer.transportProviderNationId === user?.nation && offer.userId !== user?.id
              ? offer.offerPatients?.filter(op => op.transportMode === 'PROVIDED_BY_PARTNER_NATION').map(op => op.patientId)
              : offer.offerPatients?.map(op => op.patientId)
          }
        />
      )}

      {/* Withdraw Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showWithdrawConfirm}
        title={t('offers.withdrawOffer')}
        message={t('offers.confirmReject')}
        details={[
          'Your offer will be permanently removed from this request',
          'The requesting nation will be notified',
          'You can submit a new offer later as long as the request is still published'
        ]}
        confirmLabel={t('offers.withdrawOffer')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={() => {
          setShowWithdrawConfirm(false);
          onWithdraw(offer.id);
        }}
        onCancel={() => setShowWithdrawConfirm(false)}
      />
    </>
  );
}

export default OfferItem;
